import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, parseISO, isAfter, isBefore, startOfDay } from 'date-fns'
import { sv } from 'date-fns/locale'

// Utility for merging Tailwind classes
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Date formatting utilities
export const swedishDays = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör']
export const swedishMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']

export function formatDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  return format(dateObj, 'yyyy-MM-dd', { locale: sv })
}

export function formatDateTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  return format(dateObj, 'yyyy-MM-dd HH:mm', { locale: sv })
}

export function getCalendarBadgeData(date: string | Date) {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  const dayIndex = dateObj.getDay()

  return {
    weekday: swedishDays[dayIndex],
    day: format(dateObj, 'd'),
    month: swedishMonths[dateObj.getMonth()],
    time: format(dateObj, 'HH:mm'),
  }
}

// Training status utilities
export function isTrainingPast(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  return isBefore(dateObj, startOfDay(new Date()))
}

export function isTrainingToday(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  const today = startOfDay(new Date())
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  return isAfter(dateObj, today) && isBefore(dateObj, tomorrow)
}

export function isTrainingFuture(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  return isAfter(dateObj, startOfDay(new Date()))
}

// Voucher utilities
export function formatVouchers(vouchers: number): string {
  return vouchers.toString()
}

export function getVoucherClass(vouchers: number): string {
  if (vouchers < 0) return 'text-red-600 font-semibold'
  if (vouchers <= 3) return 'text-red-600 font-semibold'
  return 'text-gray-900'
}

export function formatTrainingPlural(count: number): string {
  return count === 1 ? 'träning' : 'träningar'
}

// API utilities
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export function getApiUrl(endpoint: string): string {
  return `${API_BASE_URL}${endpoint}`
}

// Form validation utilities
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function validateDateTime(dateTime: string): boolean {
  const dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/
  return dateTimeRegex.test(dateTime)
}

// Local storage utilities
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'ntr_auth_token',
  USER_DATA: 'ntr_user_data',
} as const

export function getStoredToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
}

export function setStoredToken(token: string): void {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token)
}

export function removeStoredToken(): void {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
}

export function getStoredUser(): any {
  const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA)
  return userData ? JSON.parse(userData) : null
}

export function setStoredUser(user: any): void {
  localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user))
}

export function removeStoredUser(): void {
  localStorage.removeItem(STORAGE_KEYS.USER_DATA)
}
